import { useState, useCallback } from 'react';
import axios from 'axios';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { CalendarBlank, FileText } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { downloadPDF, downloadExcel } from '@/components/reports/reportExports';
import { ReportDisplay } from '@/components/reports/ReportDisplay';

const API = `${window.location.origin}/api`;

const getDateRange = (preset, customStart, customEnd) => {
  const today = new Date();
  switch (preset) {
    case 'today': return { start: format(today, 'dd/MM/yyyy'), end: format(today, 'dd/MM/yyyy') };
    case 'yesterday': { const d = subDays(today, 1); return { start: format(d, 'dd/MM/yyyy'), end: format(d, 'dd/MM/yyyy') }; }
    case 'last7days': return { start: format(subDays(today, 6), 'dd/MM/yyyy'), end: format(today, 'dd/MM/yyyy') };
    case 'thismonth': return { start: format(startOfMonth(today), 'dd/MM/yyyy'), end: format(endOfMonth(today), 'dd/MM/yyyy') };
    case 'lastmonth': { const lm = subMonths(today, 1); return { start: format(startOfMonth(lm), 'dd/MM/yyyy'), end: format(endOfMonth(lm), 'dd/MM/yyyy') }; }
    case 'custom': return { start: customStart, end: customEnd };
    default: return { start: format(today, 'dd/MM/yyyy'), end: format(today, 'dd/MM/yyyy') };
  }
};

export const ReportsSection = () => {
  const [dateRange, setDateRange] = useState('today');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(format(new Date(), 'dd/MM/yyyy'));
  const [customEndDate, setCustomEndDate] = useState(format(new Date(), 'dd/MM/yyyy'));

  const currentRange = useCallback(
    () => getDateRange(dateRange, customStartDate, customEndDate),
    [dateRange, customStartDate, customEndDate]
  );

  const fetchReport = async () => {
    try {
      setLoading(true);
      const { start, end } = currentRange();
      const [summaryResponse, transactionsResponse] = await Promise.all([
        axios.get(`${API}/reports/summary`, { params: { start_date: start, end_date: end } }),
        axios.get(`${API}/transactions/all`, { params: { start_date: start, end_date: end } }),
      ]);
      setReportData({ summary: summaryResponse.data, transactions: transactionsResponse.data });
      toast.success('Report generated successfully');
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-section">
      {/* Filters Card */}
      <Card className="rounded-none border border-[#E5E5E0] bg-white p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="heading text-xl font-bold text-[#111110]">Performance Reports</h2>
            <p className="text-sm text-[#666660] mt-1">Generate and download financial reports</p>
          </div>
          <FileText size={32} className="text-[#002FA7]" weight="bold" />
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#111110] mb-2 block">Date Range</label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="rounded-none border-[#E5E5E0]" data-testid="date-range-select">
                  <SelectValue placeholder="Select date range" />
                </SelectTrigger>
                <SelectContent className="rounded-none border-[#E5E5E0]">
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="last7days">Last 7 Days</SelectItem>
                  <SelectItem value="thismonth">This Month</SelectItem>
                  <SelectItem value="lastmonth">Last Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {dateRange === 'custom' && (
              <>
                <div>
                  <label className="text-sm font-medium text-[#111110] mb-2 block">Start Date</label>
                  <input type="text" placeholder="DD/MM/YYYY" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="w-full px-3 py-2 border border-[#E5E5E0] text-sm date-text" data-testid="custom-start-date" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#111110] mb-2 block">End Date</label>
                  <input type="text" placeholder="DD/MM/YYYY" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="w-full px-3 py-2 border border-[#E5E5E0] text-sm date-text" data-testid="custom-end-date" />
                </div>
              </>
            )}
          </div>
          <Button onClick={fetchReport} disabled={loading} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="generate-report-button">
            <CalendarBlank size={18} className="mr-2" />
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>
        </div>
      </Card>

      {/* Report Display */}
      <ReportDisplay
        reportData={reportData}
        onDownloadPDF={() => downloadPDF(reportData, currentRange())}
        onDownloadExcel={() => downloadExcel(reportData, currentRange())}
      />
    </div>
  );
};

export default ReportsSection;
